import type { LucideIcon } from 'lucide-react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Sparkline } from './charts/Sparkline';

export type MetricTone = 'default' | 'success' | 'warning' | 'danger' | 'shield' | 'detect' | 'trust' | 'signal';

const toneConfig: Record<MetricTone, { icon: string; accent: string; value: string }> = {
  default: {
    icon:   'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400',
    accent: 'border-t-2 border-t-primary-500',
    value:  'text-fg',
  },
  success: {
    icon:   'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
    accent: 'border-t-2 border-t-emerald-500',
    value:  'text-fg',
  },
  warning: {
    icon:   'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
    accent: 'border-t-2 border-t-amber-500',
    value:  'text-fg',
  },
  danger: {
    icon:   'bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400',
    accent: 'border-t-2 border-t-rose-500',
    value:  'text-fg',
  },
  shield: {
    icon:   'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
    accent: 'border-t-2 border-t-emerald-500',
    value:  'text-fg',
  },
  detect: {
    icon:   'bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-400',
    accent: 'border-t-2 border-t-orange-500',
    value:  'text-fg',
  },
  trust: {
    icon:   'bg-cyan-50 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-400',
    accent: 'border-t-2 border-t-cyan-500',
    value:  'text-fg',
  },
  signal: {
    icon:   'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400',
    accent: 'border-t-2 border-t-violet-500',
    value:  'text-fg',
  },
};

interface MetricCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  delta?: number | null;
  invertDelta?: boolean;
  icon: LucideIcon;
  tone?: MetricTone;
  sparkline?: number[];
  loading?: boolean;
  className?: string;
}

export function MetricCard({
  label,
  value,
  subtitle,
  delta,
  invertDelta,
  icon: Icon,
  tone = 'default',
  sparkline,
  loading = false,
  className,
}: MetricCardProps) {
  const cfg = toneConfig[tone];
  const hasDelta = delta !== undefined && delta !== null && Number.isFinite(delta);
  const isPositive = (delta ?? 0) >= 0;
  const isGood = invertDelta ? !isPositive : isPositive;

  if (loading) {
    return (
      <div className={cn('animate-pulse rounded-2xl border border-border bg-surface p-5 shadow-card dark:shadow-card-dark', cfg.accent, className)}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 space-y-2">
            <div className="h-3 w-28 rounded bg-surface-muted" />
            <div className="h-7 w-20 rounded bg-surface-muted" />
          </div>
          <div className="h-10 w-10 rounded-xl bg-surface-muted" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-2xl border border-border bg-surface p-5 shadow-card',
        'animate-card-enter transition-all duration-200 hover:shadow-card-md hover:-translate-y-[1px] dark:shadow-card-dark',
        cfg.accent,
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium uppercase tracking-wide text-fg-muted">{label}</p>
          <p className={cn('mt-2 text-[26px] font-bold leading-none tracking-tight', cfg.value)}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtitle && (
            <p className="mt-1.5 text-xs text-fg-subtle">{subtitle}</p>
          )}
          {hasDelta && delta !== undefined && delta !== null && (
            <span
              className={cn(
                'mt-2 inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                isGood
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400'
                  : 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400',
              )}
            >
              {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              {Math.abs(delta).toFixed(1)}%
            </span>
          )}
        </div>
        <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-inner-sm', cfg.icon)}>
          <Icon className="h-5 w-5" aria-hidden />
        </div>
      </div>
      {sparkline && sparkline.length > 1 && (
        <div className="mt-4 h-10 w-full">
          <Sparkline values={sparkline} tone={tone} />
        </div>
      )}
    </div>
  );
}
